/**
 * useCards — fetch and cache the Wonders card catalog from /api/cards.
 * The catalog is small (~500 KB JSON for 958 cards), so we fetch once
 * at app start and hold it in memory. Image URLs are computed from the
 * card_id against the public R2 base.
 */

import { useEffect, useState } from 'react';
import { apiPath } from '../../api.js';
import type { CardData } from '../types.js';

type Status = 'idle' | 'loading' | 'ready' | 'error';

export type CardsHook = {
  status: Status;
  cards: ReadonlyMap<string, CardData>;
  imageUrl: (cardId: string) => string;
  error: string | null;
};

const CARD_IMAGE_BASE =
  (typeof window !== 'undefined' &&
    (window as unknown as { __WONDERS_CARD_IMAGES_BASE?: string }).__WONDERS_CARD_IMAGES_BASE) ||
  'https://pub-66349c5b915245e5b2afd50f80815d23.r2.dev';

/**
 * R2 stores card images using the original mixed-case prefixes
 * (`CotS_319.webp`, `E_001.webp`). The deck-import script in scripts/
 * normalised tournament card_ids to all-uppercase (`COTS_319`), so a
 * direct lookup 404s. This translation undoes the uppercase mapping for
 * the URL only — the canonical card_id used everywhere else stays
 * upper-case to match the D1 deck rows.
 */
function normalizeCardId(cardId: string): string {
  if (cardId.startsWith('COTS_')) return `CotS_${cardId.slice(5)}`;
  return cardId;
}

let cachedCards: ReadonlyMap<string, CardData> | null = null;
let inflight: Promise<ReadonlyMap<string, CardData>> | null = null;

async function fetchCards(): Promise<ReadonlyMap<string, CardData>> {
  if (cachedCards) return cachedCards;
  if (inflight) return inflight;
  inflight = (async () => {
    const res = await fetch(apiPath('/api/cards?game_id=wonders'), {
      credentials: 'include',
    });
    if (!res.ok) {
      throw new Error(`/api/cards returned ${res.status}`);
    }
    const json = (await res.json()) as { ok: boolean; cards: readonly CardData[] };
    if (!json.ok || !Array.isArray(json.cards)) {
      throw new Error('cards response malformed');
    }
    const map = new Map<string, CardData>();
    for (const card of json.cards) {
      map.set(card.card_id, card);
    }
    cachedCards = map;
    return map;
  })().finally(() => {
    inflight = null;
  });
  return inflight;
}

export function useCards(): CardsHook {
  const [status, setStatus] = useState<Status>('idle');
  const [cards, setCards] = useState<ReadonlyMap<string, CardData>>(new Map());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    fetchCards()
      .then((map) => {
        if (cancelled) return;
        setCards(map);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'unknown error');
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return {
    status,
    cards,
    error,
    imageUrl: (cardId: string) =>
      `${CARD_IMAGE_BASE}/cards/wonders/${normalizeCardId(cardId)}.webp`,
  };
}
